import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuthBaseUrl, buildAuthUrl } from '../config/api';

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isTokenReady: boolean;
  email: string | null;
  login: (token: string) => void;
  logout: () => void;
  requestMagicLink: (email: string) => Promise<{ devLink?: string }>;
  setEmail: (email: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmailState] = useState<string | null>(null);
  const [isTokenReady, setIsTokenReady] = useState(false);

  useEffect(() => {
    console.log('AuthContext: Initializing authentication...');
    console.log('AuthContext: Current URL:', window.location.href);
    console.log('AuthContext: Search params:', window.location.search);
    
    // Check for magic link token in URL first
    const urlParams = new URLSearchParams(window.location.search);
    const magicToken = urlParams.get('token');
    console.log('AuthContext: Extracted token from URL:', magicToken);
    
    if (magicToken) {
      console.log('AuthContext: Magic link token found in URL, verifying...');
      handleMagicLinkVerification(magicToken);
      return;
    } else {
      console.log('AuthContext: No token found in URL, proceeding with other auth methods');
    }
    
    // Check for token in cookies first (set by edge auth worker)
    const cookieToken = getCookieValue('companion_auth');
    console.log('AuthContext: Cookie token found:', cookieToken ? 'YES' : 'NO');
    if (cookieToken) {
      console.log('AuthContext: Using cookie token');
      setToken(cookieToken);
      setIsTokenReady(true);
      setIsLoading(false);
      return;
    }

    // Fallback to localStorage
    const storedToken = localStorage.getItem('auth_token');
    console.log('AuthContext: localStorage token found:', storedToken ? 'YES' : 'NO');
    if (storedToken) {
      // Verify token is not expired
      try {
        const payload = JSON.parse(atob(storedToken.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        console.log('AuthContext: Token expires:', new Date(payload.exp * 1000));
        console.log('AuthContext: Token valid:', payload.exp > now);
        if (payload.exp > now) {
          console.log('AuthContext: Setting valid token');
          setToken(storedToken);
          setIsTokenReady(true);
        } else {
          console.log('AuthContext: Removing expired token');
          localStorage.removeItem('auth_token');
        }
      } catch (e) {
        console.log('AuthContext: Invalid token format, removing:', e instanceof Error ? e.message : String(e));
        localStorage.removeItem('auth_token');
      }
    }
    
    setIsLoading(false);
    console.log('AuthContext: Initialization complete');
  }, []);

  // Add token refresh monitoring and validation
  useEffect(() => {
    if (!token) {
      setIsTokenReady(false);
      return;
    }
    
    const checkTokenExpiration = () => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = payload.exp - now;
        
        // If token expires in less than 5 minutes, warn about impending expiry
        if (timeUntilExpiry < 300 && timeUntilExpiry > 0) {
          console.warn('AuthContext: Token expires soon, user should re-authenticate');
          // In a real app, you might want to show a notification
        } else if (timeUntilExpiry <= 0) {
          console.log('AuthContext: Token has expired, logging out');
          logout();
          return;
        }
        
        // Token is valid, mark as ready if not already
        if (!isTokenReady) {
          console.log('AuthContext: Token validated, marking as ready');
          setIsTokenReady(true);
        }
      } catch (error) {
        console.error('AuthContext: Error checking token expiration:', error);
        setIsTokenReady(false);
      }
    };
    
    // Check immediately
    checkTokenExpiration();
    
    // Check every 30 seconds
    const interval = setInterval(checkTokenExpiration, 30000);
    
    return () => clearInterval(interval);
  }, [token, isTokenReady]);

  // Listen for token refresh events from CompanionClient
  useEffect(() => {
    const handleTokenRefresh = (event: CustomEvent) => {
      if (event.detail?.token && event.detail.token !== token) {
        console.log('AuthContext: Received token refresh from client');
        setToken(event.detail.token);
        localStorage.setItem('auth_token', event.detail.token);
        setIsTokenReady(true);
      }
    };
    
    const handleTokenExpired = () => {
      console.log('AuthContext: Received token expiry notification');
      logout();
    };
    
    window.addEventListener('auth-token-updated', handleTokenRefresh as EventListener);
    window.addEventListener('auth-token-expired', handleTokenExpired);
    
    return () => {
      window.removeEventListener('auth-token-updated', handleTokenRefresh as EventListener);
      window.removeEventListener('auth-token-expired', handleTokenExpired);
    };
  }, [token]);

  const handleMagicLinkVerification = async (magicToken: string) => {
    try {
      console.log('AuthContext: Verifying magic link token:', magicToken);
      console.log('AuthContext: Auth URL:', buildAuthUrl('/auth/verify'));
      const response = await fetch(buildAuthUrl('/auth/verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: magicToken }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('AuthContext: Magic link verification failed:', error);
        throw new Error(error.message || 'Failed to verify magic link');
      }

      const data = await response.json();
      console.log('AuthContext: Magic link verified successfully');
      
      if (data.jwt) {
        setToken(data.jwt);
        localStorage.setItem('auth_token', data.jwt);
        setIsTokenReady(true);
        
        // Dispatch event to notify other components that auth has changed
        window.dispatchEvent(new CustomEvent('auth-token-updated', {
          detail: { token: data.jwt, isAuthenticated: true }
        }));
        
        // Clean up URL by removing the token parameter
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, document.title, url.pathname + url.search);
      }
    } catch (error) {
      console.error('AuthContext: Magic link error:', error);
      // Clean up URL even on error
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState({}, document.title, url.pathname + url.search);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get cookie value by name with enhanced parsing
   */
  const getCookieValue = (name: string): string | null => {
    try {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        const cookieValue = parts.pop()?.split(';').shift() || null;
        console.log(`AuthContext: Retrieved cookie '${name}':`, cookieValue ? 'FOUND' : 'NOT_FOUND');
        return cookieValue;
      }
    } catch (error) {
      console.error(`AuthContext: Error reading cookie '${name}':`, error);
    }
    return null;
  };

  const login = (newToken: string) => {
    console.log('AuthContext: Setting new token');
    setToken(newToken);
    localStorage.setItem('auth_token', newToken);
    setIsTokenReady(true);
    
    // Dispatch event to notify other components that auth has changed
    window.dispatchEvent(new CustomEvent('auth-token-updated', {
      detail: { token: newToken, isAuthenticated: true }
    }));
  };

  const logout = () => {
    console.log('AuthContext: Logging out and clearing all auth data');
    setToken(null);
    setEmailState(null);
    setIsTokenReady(false);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('companion_cache');
    
    // Comprehensive cookie clearing for all environments
    clearAuthCookies();
    
    // Don't redirect - just stay on the same page and show login form
    setIsLoading(false);
    
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('auth-token-updated', {
      detail: { token: null, isAuthenticated: false }
    }));
  };

  /**
   * Clear authentication cookies across all possible domains and paths
   * with proper handling for different environments and cookie attributes
   */
  const clearAuthCookies = () => {
    const cookieNames = ['companion_auth', 'auth_token', 'session_token'];
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isSecureContext = window.location.protocol === 'https:' || isLocalhost;
    
    // Build comprehensive domain list
    const domains = new Set<string>();
    
    // 1. No domain attribute (most restrictive - current host only)
    domains.add('');
    
    // 2. Current hostname
    if (hostname) {
      domains.add(hostname);
      
      // 3. Parent domains for *.pages.dev and *.workers.dev
      const parts = hostname.split('.');
      
      // For ai-companion-web.pages.dev, we need:
      // - .pages.dev (for cross-subdomain)
      // - .blomst.workers.dev (for API domain cookies)
      
      if (parts.length >= 2) {
        // Add .tld.com format
        domains.add('.' + parts.slice(-2).join('.'));
        
        // Add without leading dot as well
        domains.add(parts.slice(-2).join('.'));
      }
      
      if (parts.length >= 3) {
        // Add .subdomain.tld.com format
        domains.add('.' + parts.slice(-3).join('.'));
        
        // Add without leading dot
        domains.add(parts.slice(-3).join('.'));
      }
    }
    
    // 4. Known production and development domains
    const knownDomains = [
      // Cloudflare Pages domains
      '.pages.dev',
      'pages.dev',
      'ai-companion-web.pages.dev',
      '.ai-companion-web.pages.dev',
      
      // Cloudflare Workers domains
      '.workers.dev',
      'workers.dev',
      '.blomst.workers.dev',
      'blomst.workers.dev',
      'ai-companion-edge-auth-production.blomst.workers.dev',
      'ai-companion-production.blomst.workers.dev',
      
      // Localhost variations
      'localhost',
      '.localhost',
      '127.0.0.1',
      '[::1]', // IPv6 localhost
    ];
    
    knownDomains.forEach(domain => domains.add(domain));
    
    // 5. All possible paths where cookies might be set
    const paths = [
      '/',
      '/auth',
      '/api',
      '/api/chat',
      '/auth/magic-link',
      '/auth/verify',
      '' // No path attribute
    ];
    
    console.log('AuthContext: Clearing cookies for domains:', Array.from(domains));
    console.log('AuthContext: Secure context:', isSecureContext);
    
    // Clear cookies with all combinations
    cookieNames.forEach(cookieName => {
      domains.forEach(domain => {
        paths.forEach(path => {
          // Build the cookie clearing strings
          const baseExpiry = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
          const maxAge = `; max-age=0`; // Alternative to expires
          
          // For each domain/path combination, try multiple clearing strategies
          const cookieStrings: string[] = [];
          
          if (domain) {
            // With domain attribute
            if (isSecureContext) {
              // HTTPS or localhost - can use Secure
              cookieStrings.push(
                `${baseExpiry}${maxAge}; path=${path || '/'}; domain=${domain}; SameSite=None; Secure`,
                `${baseExpiry}${maxAge}; path=${path || '/'}; domain=${domain}; SameSite=Lax; Secure`,
                `${baseExpiry}${maxAge}; path=${path || '/'}; domain=${domain}; Secure`
              );
            }
            
            // Without Secure (for HTTP contexts)
            cookieStrings.push(
              `${baseExpiry}${maxAge}; path=${path || '/'}; domain=${domain}; SameSite=Lax`,
              `${baseExpiry}${maxAge}; path=${path || '/'}; domain=${domain}; SameSite=None`,
              `${baseExpiry}${maxAge}; path=${path || '/'}; domain=${domain}`
            );
          } else {
            // Without domain attribute (current host only)
            if (isSecureContext) {
              cookieStrings.push(
                `${baseExpiry}${maxAge}; path=${path || '/'}; SameSite=None; Secure`,
                `${baseExpiry}${maxAge}; path=${path || '/'}; SameSite=Lax; Secure`,
                `${baseExpiry}${maxAge}; path=${path || '/'}; Secure`
              );
            }
            
            cookieStrings.push(
              `${baseExpiry}${maxAge}; path=${path || '/'}; SameSite=Lax`,
              `${baseExpiry}${maxAge}; path=${path || '/'}; SameSite=None`,
              `${baseExpiry}${maxAge}; path=${path || '/'}`
            );
          }
          
          // Execute all clearing attempts
          cookieStrings.forEach(cookieString => {
            try {
              document.cookie = cookieString;
            } catch (e) {
              // Some combinations might fail due to browser restrictions
              console.debug(`Failed to clear cookie with: ${cookieString}`, e);
            }
          });
        });
      });
    });
    
    // Verify cookies are actually cleared
    const remainingCookies = document.cookie
      .split(';')
      .filter(cookie => {
        const trimmed = cookie.trim();
        return cookieNames.some(name => trimmed.startsWith(name + '='));
      });
    
    if (remainingCookies.length > 0) {
      console.warn('AuthContext: Some cookies could not be cleared:', remainingCookies);
    } else {
      console.log('AuthContext: All authentication cookies cleared successfully');
    }
  };

  const requestMagicLink = async (emailAddress: string) => {
    try {
      const response = await fetch(buildAuthUrl('/auth/magic-link'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailAddress }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send magic link');
      }

      const data = await response.json();
      setEmailState(emailAddress);
      return { devLink: data.devLink };
    } catch (error) {
      throw error;
    }
  };

  const setEmail = (emailAddress: string) => {
    setEmailState(emailAddress);
  };

  return (
    <AuthContext.Provider value={{
      token,
      isAuthenticated: !!token && isTokenReady,
      isLoading,
      isTokenReady,
      email,
      login,
      logout,
      requestMagicLink,
      setEmail
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}